import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { Messages } from '../../../codegen/Localize';
import { IJobHistoryItemVM } from './IJobHistory';
import { JobHistoryTable } from './JobHistoryTable';
import { TaskDetailsSpaceStyles } from './TaskDetailsSpaceStyles';
import { TaskMetadataSection } from './TaskMetadataSection';
import { ITaskDetailsViewModel } from './types/ITaskDetails';

export const TaskDetailsSpaceTestIds = {
  Header: 'task-details-header',
  Spinner: 'task-details-spinner',
  ErrorReturnButton: 'task-details-error-return-button',
  MetadataSection: 'task-details-metadata-section',
  HistoryPlaceholder: 'task-details-history-placeholder',
  EmptyState: 'task-details-empty-state',
  RunNowButton: 'task-details-run-now-button',
};

export interface TaskDetailsSpaceProps {
  /** LUID of the task being shown, parsed from the route. */
  readonly taskLuid: string;
  readonly isLoading: boolean;
  readonly hasError: boolean;
  /** Task metadata to render; null when loading, errored, or empty. */
  readonly taskDetails: ITaskDetailsViewModel | null;
  readonly jobHistoryItems: ReadonlyArray<IJobHistoryItemVM>;
  readonly isLoadingMoreHistory: boolean;
  readonly hasMoreHistory: boolean;
  readonly hasHistoryLoadError: boolean;
  readonly onLoadMoreHistory: () => void;
  readonly onRetryLoadMoreHistory: () => void;
  /** Invoked by the error-state return button. */
  readonly onReturnToJobs: () => void;
  /**
   * Whether to offer the "Run Now" action for this task (Epic 264 stretch). True only once the task
   * metadata has loaded and its type supports a per-task run-now (Extract / Bridge / Flow); the
   * container computes this via `TaskRunNowService.canRunNow`.
   */
  readonly canRunNow: boolean;
  /** True while a Run Now request is in flight; shows a spinner on the button and blocks re-clicks. */
  readonly isRunningNow: boolean;
  /**
   * Whether the Run Now button should be shown but disabled (greyed), independent of the in-flight
   * spinner. Mirrors the Tasks-tab menu, whose Run Now item stays visible but is disabled when the
   * server/site Run Now setting is off (see the container's `isRunNowEnabledBySetting`).
   */
  readonly runNowDisabled: boolean;
  /** Enqueues a Run Now for this task. Only wired when {@link canRunNow} is true. */
  readonly onRunNow: () => void;
  /** Id of the job that navigated here, parsed from the route's `jobId` query param. Consumed by S08-b for autoscroll. */
  readonly jobId?: string | null;
  /** Requests another page of history when `jobId` hasn't been found in the currently loaded jobs. */
  readonly onRequestMoreForJob?: () => void;
}

/**
 * Presentational shell for the Task Details page.
 *
 * Renders inside the standard space chrome (masthead + subplace area) and lays out two sections:
 * a fixed task metadata section and a scrollable job run history section.
 * Loading, error, and empty states are handled here.
 */
export class TaskDetailsSpace extends React.PureComponent<TaskDetailsSpaceProps> {
  public render() {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={SpaceContainerStyles.MastheadArea}>
          <SpaceHeaderContent headerText={Messages.taskDetailsBrowserTitle()} testId={TaskDetailsSpaceTestIds.Header} />
          {this.renderActions()}
        </div>
        <div className={TaskDetailsSpaceStyles.HeaderDivider} />
        <div className={SpaceContainerStyles.SubplaceArea}>
          {this.renderBody()}
        </div>
      </div>
    );
  }

  // Task-action bar directly beneath the title (Figma). Rendered only for task types that support a
  // per-task run-now; the button spins and is disabled while a request is in flight.
  private renderActions(): JSX.Element | null {
    if (!this.props.canRunNow) {
      return null;
    }
    return (
      <div className={TaskDetailsSpaceStyles.ActionsRow}>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          onClick={this.props.onRunNow}
          disabled={this.props.isRunningNow || this.props.runNowDisabled}
          showSpinner={this.props.isRunningNow}
          testId={TaskDetailsSpaceTestIds.RunNowButton}
        >
          {Messages.runNow()}
        </ButtonWidget>
      </div>
    );
  }

  private renderBody(): JSX.Element {
    if (this.props.isLoading) {
      return this.renderLoading();
    }
    if (this.props.hasError) {
      return this.renderError();
    }
    if (!this.props.taskDetails) {
      return this.renderTaskNotFound();
    }
    return this.renderContent();
  }

  private renderLoading(): JSX.Element {
    return (
      <div className={TaskDetailsSpaceStyles.CenteredState}>
        <ActivitySpinnerWidget testId={TaskDetailsSpaceTestIds.Spinner} spinnerSize={ActivitySpinnerSize.Large} />
      </div>
    );
  }

  private renderError(): JSX.Element {
    return (
      <div className={TaskDetailsSpaceStyles.CenteredState}>
        <div className={TaskDetailsSpaceStyles.StateMessage}>{Messages.taskDetailsErrorMessage()}</div>
        <ButtonWidget
          buttonType={ButtonType.Primary}
          onClick={this.props.onReturnToJobs}
          testId={TaskDetailsSpaceTestIds.ErrorReturnButton}
        >
          {Messages.taskDetailsReturnToJobs()}
        </ButtonWidget>
      </div>
    );
  }

  private renderTaskNotFound(): JSX.Element {
    return (
      <div className={TaskDetailsSpaceStyles.CenteredState}>
        <div className={TaskDetailsSpaceStyles.NotFoundContainer}>
          <h1 className={TaskDetailsSpaceStyles.NotFoundHeading}>
            {Messages.taskNotFoundHeading()}
          </h1>
          <p className={TaskDetailsSpaceStyles.NotFoundDescription}>
            {Messages.taskNotFoundDescription()}
          </p>
          <ButtonWidget
            buttonType={ButtonType.Primary}
            onClick={this.props.onReturnToJobs}
          >
            {Messages.taskNotFoundReturnLink()}
          </ButtonWidget>
        </div>
      </div>
    );
  }

  private renderContent(): JSX.Element {
    return (
      <div className={TaskDetailsSpaceStyles.ContentFill}>
        <div className={TaskDetailsSpaceStyles.MetadataSection}>
          {this.renderMetadata()}
        </div>
        <div className={TaskDetailsSpaceStyles.HistorySection}>
          <div className={TaskDetailsSpaceStyles.HistoryHeading}>{Messages.taskDetailsJobHistoryHeading()}</div>
          <JobHistoryTable
            // Remount per task so the table's persistent refs (scroll/highlight/load state) don't
            // leak across task navigation (the container reuses this instance).
            key={this.props.taskLuid}
            jobs={this.props.jobHistoryItems}
            isLoadingMore={this.props.isLoadingMoreHistory}
            hasMore={this.props.hasMoreHistory}
            hasLoadError={this.props.hasHistoryLoadError}
            onLoadMore={this.props.onLoadMoreHistory}
            onRetryLoadMore={this.props.onRetryLoadMoreHistory}
            jobId={this.props.jobId}
            onRequestNextPageForJob={this.props.onRequestMoreForJob}
          />
        </div>
      </div>
    );
  }

  private renderMetadata(): JSX.Element {
    if (!this.props.taskDetails) {
      return (
        <div className={TaskDetailsSpaceStyles.MetadataPlaceholder} data-tb-test-id={TaskDetailsSpaceTestIds.EmptyState}>
          {Messages.taskDetailsEmptyMessage()}
        </div>
      );
    }
    return (
      <div data-tb-test-id={TaskDetailsSpaceTestIds.MetadataSection}>
        <TaskMetadataSection taskDetails={this.props.taskDetails} />
      </div>
    );
  }
}
